import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { palette } from '../../utils/palette';

// Seasonal heading accent colors
const SEASON_SPRING_COLOR = palette.vividYellow; // #FFC107
const SEASON_SUMMER_COLOR = '#87CEEB'; // Sky blue
const SEASON_AUTUMN_COLOR = '#FF8C00'; // Dark orange
const SEASON_WINTER_COLOR = '#1E3A8A'; // Dark blue
import { RootStackParamList } from '../../types/navigation';
import { useTheme } from '../../hooks/useTheme';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  getArticleById,
  getRelatedArticles,
  BlogArticle,
} from '../../data/blogArticles';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { LinearGradient } from 'expo-linear-gradient';
import { hexToRgba } from '../../utils/colorUtils';

type BlogArticleRouteProp = RouteProp<RootStackParamList, 'BlogArticle'>;

const BlogArticleScreen = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<BlogArticleRouteProp>();
  const { articleId } = route.params;
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { isMobile, screenWidth } = useResponsiveLayout();

  const [article, setArticle] = useState<BlogArticle | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Defer state updates to avoid synchronous setState in effect
    const id = setTimeout(() => {
      const articleData = getArticleById(articleId);
      if (articleData) {
        setArticle(articleData);
        const related = getRelatedArticles(articleId);
        setRelatedArticles(related);
      }
      setLoading(false);
    }, 0);
    return () => clearTimeout(id);
  }, [articleId]);

  useEffect(() => {
    if (article) {
      navigation.setOptions({
        title: article.title,
        headerShown: !isMobile || Platform.OS === 'web',
      });
    }
  }, [navigation, article, isMobile]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for future use
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const parseMarkdownContent = (content: string) => {
    const sections: string[] = [];
    const lines = content.split('\n');
    let currentSection = '';
    let inTable = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const isTableLine = line.trim().startsWith('|') && line.includes('|');
      const isEmpty = line.trim() === '';

      if (isTableLine) {
        if (!inTable && currentSection.trim()) {
          // Save previous section
          sections.push(currentSection.trim());
          currentSection = '';
        }
        inTable = true;
        currentSection += line + '\n';
      } else if (inTable && isEmpty) {
        // End of table
        if (currentSection.trim()) {
          sections.push(currentSection.trim());
          currentSection = '';
        }
        inTable = false;
      } else if (inTable) {
        // Still in table but not a table line (shouldn't happen, but handle it)
        currentSection += line + '\n';
      } else {
        // Regular content
        if (isEmpty && currentSection.trim()) {
          sections.push(currentSection.trim());
          currentSection = '';
        } else if (!isEmpty) {
          currentSection += line + '\n';
        }
      }
    }

    // Add remaining content
    if (currentSection.trim()) {
      sections.push(currentSection.trim());
    }

    return sections;
  };

  const renderTable = (tableText: string) => {
    const lines = tableText.split('\n').filter((line) => line.trim());
    if (lines.length < 2) return null;

    // Parse header (line 0) and separator (line 1), data starts at line 2
    const headerLine = lines[0];
    const dataLines = lines.slice(2);

    const parseRow = (row: string): string[] => {
      return row
        .split('|')
        .map((cell) => cell.trim())
        .filter((cell) => cell.length > 0);
    };

    const headers = parseRow(headerLine);
    const rows = dataLines.map(parseRow);

    return (
      <View
        key={tableText}
        style={[styles.tableContainer, { borderColor: colors.primary + '30' }]}
      >
        <View
          style={[
            styles.tableRow,
            styles.tableHeader,
            {
              backgroundColor: colors.primary + '15',
              borderColor: colors.primary + '30',
            },
          ]}
        >
          {headers.map((header, index) => (
            <View
              key={index}
              style={[
                styles.tableCell,
                styles.tableHeaderCell,
                index === headers.length - 1 && styles.tableCellLast,
                { borderColor: colors.primary + '30' },
              ]}
            >
              <Text style={[styles.tableHeaderText, { color: colors.primary }]}>
                {header}
              </Text>
            </View>
          ))}
        </View>
        {rows.map((row, rowIndex) => (
          <View
            key={rowIndex}
            style={[
              styles.tableRow,
              { borderColor: colors.primary + '15' },
              rowIndex === rows.length - 1 && { borderBottomWidth: 0 },
            ]}
          >
            {row.map((cell, cellIndex) => {
              // Handle line breaks in cells (support <br> tags)
              const cellText = cell.replace(/<br\s*\/?>/gi, '\n');
              return (
                <View
                  key={cellIndex}
                  style={[
                    styles.tableCell,
                    cellIndex === row.length - 1 && styles.tableCellLast,
                    { borderColor: colors.primary + '15' },
                  ]}
                >
                  <Text style={[styles.tableCellText, { color: colors.text }]}>
                    {cellText}
                  </Text>
                </View>
              );
            })}
          </View>
        ))}
      </View>
    );
  };

  const renderContentBlock = (block: string) => {
    // Handle tables (check for markdown table format)
    if (
      block.includes('|') &&
      block.split('\n').some((line) => line.trim().startsWith('|'))
    ) {
      return renderTable(block);
    }

    // Handle headers
    if (block.startsWith('# ')) {
      return (
        <Text key={block} style={[styles.h1, { color: colors.text }]}>
          {block.replace(/^# /, '')}
        </Text>
      );
    }
    if (block.startsWith('## ')) {
      const headingText = block.replace(/^## /, '').trim();
      let headingColor = colors.text;

      // Apply primary color to specific headings
      if (
        headingText === 'Embracing Your Perfect Palette' ||
        headingText === 'BUT WANT THE EASY WAY?' ||
        headingText === 'EITHER WAY…' ||
        headingText === 'Colour Palettes for Junk Journaling'
      ) {
        headingColor = colors.primary;
      }

      return (
        <Text key={block} style={[styles.h2, { color: headingColor }]}>
          {headingText}
        </Text>
      );
    }
    if (block.startsWith('### ')) {
      const headingText = block.replace(/^### /, '').trim();
      let headingColor = colors.text;

      // Apply season-specific colors
      if (headingText === 'Spring') {
        headingColor = SEASON_SPRING_COLOR;
      } else if (headingText === 'Summer') {
        headingColor = SEASON_SUMMER_COLOR;
      } else if (headingText === 'Autumn') {
        headingColor = SEASON_AUTUMN_COLOR;
      } else if (headingText === 'Winter') {
        headingColor = SEASON_WINTER_COLOR;
      }

      return (
        <Text key={block} style={[styles.h3, { color: headingColor }]}>
          {headingText}
        </Text>
      );
    }
    // Handle lists
    if (block.includes('**')) {
      // Check if this is a numbered bold item (like "**1. Characteristics:**")
      const lines = block.split('\n').filter((line) => line.trim());
      const isNumberedBoldItem = lines.some((line) =>
        /^\s*\*\*\d+\.\s/.test(line.trim())
      );

      if (isNumberedBoldItem) {
        // Render numbered items with hanging indent
        return (
          <View key={block} style={styles.indentedBlock}>
            {lines.map((line, lineIndex) => {
              const trimmedLine = line.trim();

              // Check if this is a numbered bold item line
              if (/^\*\*\d+\.\s/.test(trimmedLine)) {
                // Match pattern: **1. Bold text:** description on same line
                const sameLineMatch = trimmedLine.match(
                  /^\*\*(\d+\.\s[^*:]+):\*\*(.*)$/
                );
                if (sameLineMatch) {
                  const boldText = sameLineMatch[1] + ':'; // "1. Characteristics:"
                  const descText = sameLineMatch[2].trim(); // "Warm and bright colors..."

                  return (
                    <View key={lineIndex} style={styles.hangingIndentItem}>
                      <View style={styles.hangingIndentContainer}>
                        <Text
                          style={[
                            styles.bodyText,
                            styles.hangingIndentPrefix,
                            { color: colors.text },
                          ]}
                        >
                          <Text style={styles.boldText}>{boldText}</Text>
                        </Text>
                        {descText && (
                          <Text
                            style={[
                              styles.bodyText,
                              styles.hangingIndentText,
                              { color: colors.text },
                            ]}
                          >
                            {descText}
                          </Text>
                        )}
                      </View>
                    </View>
                  );
                }

                // Match pattern: **1. Bold text:** (description on next line)
                const nextLineMatch = trimmedLine.match(
                  /^\*\*(\d+\.\s[^*]+)\*\*$/
                );
                if (nextLineMatch) {
                  const boldText = nextLineMatch[1]; // "1. Analyze your undertone"

                  // Check if next line exists and is descriptive text (not another bold item)
                  const nextLine = lines[lineIndex + 1];
                  const hasDescription =
                    nextLine &&
                    !/^\*\*\d+\.\s/.test(nextLine.trim()) &&
                    !nextLine.trim().startsWith('**') &&
                    nextLine.trim().length > 0;

                  return (
                    <View key={lineIndex} style={styles.hangingIndentItem}>
                      <View style={styles.hangingIndentContainer}>
                        <Text
                          style={[
                            styles.bodyText,
                            styles.hangingIndentPrefix,
                            { color: colors.text },
                          ]}
                        >
                          <Text style={styles.boldText}>{boldText}</Text>
                        </Text>
                        {hasDescription && (
                          <Text
                            style={[
                              styles.bodyText,
                              styles.hangingIndentText,
                              { color: colors.text },
                            ]}
                          >
                            {nextLine.trim()}
                          </Text>
                        )}
                      </View>
                    </View>
                  );
                }
              }

              // Skip lines that are descriptions (they're handled above)
              if (lineIndex > 0) {
                const prevLine = lines[lineIndex - 1];
                if (prevLine && /^\*\*\d+\.\s/.test(prevLine.trim())) {
                  return null; // This line was already rendered as description
                }
              }

              // Fallback for other bold text
              const parts = trimmedLine.split(/(\*\*[^*]+\*\*)/g);
              return (
                <Text
                  key={lineIndex}
                  style={[styles.bodyText, { color: colors.text }]}
                >
                  {parts.map((part, index) =>
                    part.startsWith('**') && part.endsWith('**') ? (
                      <Text key={index} style={styles.boldText}>
                        {part.replace(/\*\*/g, '')}
                      </Text>
                    ) : (
                      part
                    )
                  )}
                </Text>
              );
            })}
          </View>
        );
      }

      // Regular bold text handling
      const parts = block.split(/(\*\*[^*]+\*\*)/g);
      return (
        <Text key={block} style={[styles.bodyText, { color: colors.text }]}>
          {parts.map((part, index) =>
            part.startsWith('**') && part.endsWith('**') ? (
              <Text key={index} style={styles.boldText}>
                {part.replace(/\*\*/g, '')}
              </Text>
            ) : (
              part
            )
          )}
        </Text>
      );
    }
    // Check for plain numbered items (like "1. Enhanced appearance...")
    const lines = block.split('\n');
    const isPlainNumberedItem = lines.some((line) =>
      /^\d+\.\s/.test(line.trim())
    );

    if (isPlainNumberedItem && !block.includes('**')) {
      return (
        <View key={block} style={styles.indentedBlock}>
          <Text style={[styles.bodyText, { color: colors.text }]}>{block}</Text>
        </View>
      );
    }

    // Regular paragraphs
    if (block.trim() && !block.startsWith('-') && !block.startsWith('•')) {
      return (
        <Text key={block} style={[styles.bodyText, { color: colors.text }]}>
          {block}
        </Text>
      );
    }
    // List items
    if (block.includes('-') || block.includes('•')) {
      const items = block
        .split(/\n/)
        .filter(
          (item) => item.trim().startsWith('-') || item.trim().startsWith('•')
        );
      return (
        <View key={block} style={styles.listContainer}>
          {items.map((item, index) => (
            <View key={index} style={styles.listItem}>
              <Text style={[styles.listBullet, { color: colors.primary }]}>
                •
              </Text>
              <Text style={[styles.listText, { color: colors.text }]}>
                {item.replace(/^[-•]\s*/, '')}
              </Text>
            </View>
          ))}
        </View>
      );
    }
    return null;
  };

  if (loading || !article) {
    return (
      <LinearGradient
        colors={[
          hexToRgba(colors.appGradient1, 0.94),
          hexToRgba(colors.appGradient2, 0.94),
        ]}
        style={styles.container}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
      >
        <View style={[styles.container, { backgroundColor: 'transparent' }]}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </LinearGradient>
    );
  }

  return (
    <LinearGradient
      colors={[
        hexToRgba(colors.appGradient1, 0.94),
        hexToRgba(colors.appGradient2, 0.94),
      ]}
      style={styles.container}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
    >
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.contentContainer,
          { paddingBottom: insets.bottom + 16 },
        ]}
        showsVerticalScrollIndicator={false}
      >
        <View
          style={[
            styles.contentWrapper,
            {
              maxWidth: isMobile ? '100%' : Math.min(screenWidth * 0.7, 900),
              alignSelf: 'center',
              width: '100%',
            },
          ]}
        >
          {/* Hero Image */}
          <Image
            source={{ uri: article.image }}
            style={styles.heroImage}
            resizeMode="cover"
          />

          {/* Article Header */}
          <View
            style={[
              styles.header,
              { backgroundColor: colors.card },
              isMobile && { paddingHorizontal: 16 },
            ]}
          >
            <View style={styles.categoryBadge}>
              <Text style={[styles.categoryText, { color: colors.primary }]}>
                {article.category}
              </Text>
            </View>

            <Text style={[styles.title, { color: colors.primary }]}>
              {article.title}
            </Text>

            <Text style={[styles.excerpt, { color: colors.secondary }]}>
              {article.excerpt}
            </Text>

            {/* Meta Information */}
            <View style={styles.metaContainer}>
              <View style={styles.metaRow}>
                <View style={styles.metaInfo}>
                  <Ionicons
                    name="time-outline"
                    size={14}
                    color={colors.secondary}
                  />
                  <Text style={[styles.metaText, { color: colors.secondary }]}>
                    {article.readTime} min read
                  </Text>
                </View>
              </View>
            </View>

            {/* Tags */}
            <View style={styles.tagsContainer}>
              {article.tags.map((tag, index) => (
                <View
                  key={index}
                  style={[
                    styles.tag,
                    {
                      backgroundColor: colors.primary + '15',
                      borderColor: colors.primary + '30',
                    },
                  ]}
                >
                  <Text style={[styles.tagText, { color: colors.primary }]}>
                    #{tag}
                  </Text>
                </View>
              ))}
            </View>
          </View>

          {/* Article Content */}
          <View
            style={[
              styles.content,
              { backgroundColor: colors.card },
              isMobile && { paddingHorizontal: 16 },
            ]}
          >
            {parseMarkdownContent(article.content).map((block, index) => (
              <View key={index} style={styles.contentBlock}>
                {renderContentBlock(block)}
              </View>
            ))}
          </View>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <View style={styles.relatedContainer}>
              <Text style={[styles.relatedTitle, { color: colors.text }]}>
                Related Articles
              </Text>
              {relatedArticles.map((relatedArticle) => {
                const handleRelatedArticlePress = () => {
                  if (Platform.OS === 'web' && typeof window !== 'undefined') {
                    // Open in new tab on web
                    window.open(`/blog/${relatedArticle.id}`, '_blank');
                  } else {
                    // Navigate normally on mobile
                    navigation.push('BlogArticle', {
                      articleId: relatedArticle.id,
                    });
                  }
                };

                return (
                  <TouchableOpacity
                    key={relatedArticle.id}
                    style={[
                      styles.relatedCard,
                      { backgroundColor: colors.card },
                    ]}
                    onPress={handleRelatedArticlePress}
                    activeOpacity={0.7}
                  >
                    <Image
                      source={{ uri: relatedArticle.image }}
                      style={styles.relatedImage}
                      resizeMode="cover"
                    />
                    <View style={styles.relatedContent}>
                      <Text
                        style={[
                          styles.relatedCardTitle,
                          { color: colors.text },
                        ]}
                      >
                        {relatedArticle.title}
                      </Text>
                      <Text
                        style={[
                          styles.relatedCardExcerpt,
                          { color: colors.secondary },
                        ]}
                        numberOfLines={2}
                      >
                        {relatedArticle.excerpt}
                      </Text>
                      <View style={styles.relatedMeta}>
                        <Text
                          style={[
                            styles.relatedMetaText,
                            { color: colors.secondary },
                          ]}
                        >
                          {relatedArticle.readTime} min read
                        </Text>
                        <Ionicons
                          name="chevron-forward"
                          size={16}
                          color={colors.secondary}
                        />
                      </View>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          )}
        </View>
      </ScrollView>
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: 16,
  },
  contentWrapper: {
    paddingHorizontal: 0,
  },
  heroImage: {
    width: '100%',
    height: 180,
  },
  header: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.05)',
    paddingHorizontal: 60,
  },
  categoryBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 8,
    marginBottom: 8,
  },
  categoryText: {
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontFamily: Platform.OS === 'web' ? 'Poppins, sans-serif' : 'Poppins',
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    lineHeight: 28,
    marginBottom: 8,
    fontFamily: Platform.OS === 'web' ? 'Poppins, sans-serif' : 'Poppins',
  },
  excerpt: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 12,
    fontFamily: Platform.OS === 'web' ? 'Poppins, sans-serif' : 'Poppins',
  },
  metaContainer: {
    marginTop: 10,
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  authorInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  authorAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  authorAvatarPlaceholder: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  authorInitials: {
    fontSize: 12,
    fontWeight: '700',
  },
  authorName: {
    fontSize: 12,
    fontWeight: '600',
  },
  metaInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metaText: {
    fontSize: 11,
    fontWeight: '500',
    fontFamily: Platform.OS === 'web' ? 'Poppins, sans-serif' : 'Poppins',
  },
  dateContainer: {
    marginTop: 4,
  },
  dateText: {
    fontSize: 11,
    fontWeight: '500',
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 10,
  },
  tag: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    borderWidth: 1,
  },
  tagText: {
    fontSize: 10,
    fontWeight: '600',
    fontFamily: Platform.OS === 'web' ? 'Poppins, sans-serif' : 'Poppins',
  },
  content: {
    padding: 16,
    paddingHorizontal: 60,
    marginTop: 4,
  },
  contentBlock: {
    marginBottom: 12,
    paddingHorizontal: 0,
  },
  indentedBlock: {
    paddingLeft: 20,
    marginBottom: 4,
  },
  hangingIndentItem: {
    marginBottom: 6,
  },
  hangingIndentContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  hangingIndentPrefix: {
    flexShrink: 0,
  },
  hangingIndentText: {
    flex: 1,
    flexShrink: 1,
  },
  h1: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 8,
    marginTop: 4,
    fontFamily: Platform.OS === 'web' ? 'Poppins, sans-serif' : 'Poppins',
  },
  h2: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 6,
    marginTop: 8,
    fontFamily: Platform.OS === 'web' ? 'Poppins, sans-serif' : 'Poppins',
  },
  h3: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
    marginTop: 8,
    fontFamily: Platform.OS === 'web' ? 'Poppins, sans-serif' : 'Poppins',
  },
  bodyText: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 10,
    fontFamily: Platform.OS === 'web' ? 'Poppins, sans-serif' : 'Poppins',
  },
  boldText: {
    fontWeight: '700',
    fontFamily: Platform.OS === 'web' ? 'Poppins, sans-serif' : 'Poppins',
  },
  listContainer: {
    marginVertical: 6,
  },
  listItem: {
    flexDirection: 'row',
    marginBottom: 6,
    paddingLeft: 4,
  },
  listBullet: {
    fontSize: 14,
    marginRight: 6,
    fontWeight: '700',
    fontFamily: Platform.OS === 'web' ? 'Poppins, sans-serif' : 'Poppins',
  },
  listText: {
    fontSize: 14,
    lineHeight: 20,
    flex: 1,
    fontFamily: Platform.OS === 'web' ? 'Poppins, sans-serif' : 'Poppins',
  },
  tableContainer: {
    marginVertical: 12,
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 1,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
  },
  tableHeader: {
    borderBottomWidth: 2,
  },
  tableCell: {
    flex: 1,
    padding: 10,
    borderRightWidth: 1,
  },
  tableCellLast: {
    borderRightWidth: 0,
  },
  tableHeaderCell: {
    borderRightWidth: 1,
  },
  tableHeaderText: {
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontFamily: Platform.OS === 'web' ? 'Poppins, sans-serif' : 'Poppins',
  },
  tableCellText: {
    fontSize: 13,
    lineHeight: 18,
    fontFamily: Platform.OS === 'web' ? 'Poppins, sans-serif' : 'Poppins',
  },
  relatedContainer: {
    padding: 16,
    marginTop: 4,
  },
  relatedTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 12,
    fontFamily: Platform.OS === 'web' ? 'Poppins, sans-serif' : 'Poppins',
  },
  relatedCard: {
    flexDirection: 'row',
    borderRadius: 10,
    overflow: 'hidden',
    marginBottom: 10,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  relatedImage: {
    width: 80,
    height: 80,
  },
  relatedContent: {
    flex: 1,
    padding: 10,
    justifyContent: 'space-between',
  },
  relatedCardTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
    fontFamily: Platform.OS === 'web' ? 'Poppins, sans-serif' : 'Poppins',
  },
  relatedCardExcerpt: {
    fontSize: 12,
    lineHeight: 16,
    marginBottom: 6,
    fontFamily: Platform.OS === 'web' ? 'Poppins, sans-serif' : 'Poppins',
  },
  relatedMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  relatedMetaText: {
    fontSize: 11,
    fontWeight: '500',
    fontFamily: Platform.OS === 'web' ? 'Poppins, sans-serif' : 'Poppins',
  },
});

export default BlogArticleScreen;
